import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Eye, EyeOff } from 'lucide-react';
import { apiService } from '../../services/api';
import { toast } from '../../hooks/use-toast';

const NewsManagement = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingNews, setEditingNews] = useState(null);
  const [showForm, setShowForm] = useState(false);

  useEffect(() => {
    loadNews();
  }, []);

  const loadNews = async () => {
    try {
      const response = await apiService.getNews(false);
      setNews(response.data);
    } catch (error) {
      toast({
        title: 'Hata',
        description: 'Haberler yüklenemedi.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Bu haberi silmek istediğinizden emin misiniz?')) return;
    
    try {
      await apiService.deleteNews(id);
      toast({ title: 'Başarılı', description: 'Haber silindi.' });
      loadNews();
    } catch (error) {
      toast({
        title: 'Hata',
        description: 'Haber silinemedi.',
        variant: 'destructive',
      });
    }
  };

  const handleEdit = (newsItem) => {
    setEditingNews(newsItem);
    setShowForm(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    
    const newsData = {
      title_tr: formData.get('title_tr'),
      title_en: formData.get('title_en'),
      excerpt_tr: formData.get('excerpt_tr'),
      excerpt_en: formData.get('excerpt_en'),
      content_tr: formData.get('content_tr'),
      content_en: formData.get('content_en'),
      image_url: formData.get('image_url'),
      is_published: formData.get('is_published') === 'on',
    };

    try {
      if (editingNews) {
        await apiService.updateNews(editingNews.id, newsData);
        toast({ title: 'Başarılı', description: 'Haber güncellendi.' });
      } else {
        await apiService.createNews(newsData);
        toast({ title: 'Başarılı', description: 'Haber eklendi.' });
      }
      setShowForm(false);
      setEditingNews(null);
      loadNews();
    } catch (error) {
      toast({
        title: 'Hata',
        description: 'İşlem tamamlanamadı.',
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return <div className="text-center py-8">Yükleniyor...</div>;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#36344D]">Haberler Yönetimi</h2>
        <button
          onClick={() => {
            setEditingNews(null);
            setShowForm(true);
          }}
          className="bg-[#DF6A6A] text-white px-4 py-2 rounded-md hover:bg-[#c55555] flex items-center space-x-2"
        >
          <Plus className="w-5 h-5" />
          <span>Yeni Haber</span>
        </button>
      </div>

      {showForm ? (
        <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow-md mb-6">
          <h3 className="text-xl font-bold mb-4">
            {editingNews ? 'Haber Düzenle' : 'Yeni Haber Ekle'}
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block font-semibold mb-2">Başlık (TR)</label>
              <input
                name="title_tr"
                defaultValue={editingNews?.title_tr}
                required
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block font-semibold mb-2">Title (EN)</label>
              <input
                name="title_en"
                defaultValue={editingNews?.title_en}
                required
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block font-semibold mb-2">Özet (TR)</label>
              <textarea
                name="excerpt_tr"
                defaultValue={editingNews?.excerpt_tr}
                required
                rows="3"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block font-semibold mb-2">Excerpt (EN)</label>
              <textarea
                name="excerpt_en"
                defaultValue={editingNews?.excerpt_en}
                required
                rows="3"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block font-semibold mb-2">İçerik (TR)</label>
              <textarea
                name="content_tr"
                defaultValue={editingNews?.content_tr}
                required
                rows="5"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
            <div>
              <label className="block font-semibold mb-2">Content (EN)</label>
              <textarea
                name="content_en"
                defaultValue={editingNews?.content_en}
                required
                rows="5"
                className="w-full px-3 py-2 border rounded-md"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block font-semibold mb-2">Görsel URL</label>
            <input
              name="image_url"
              defaultValue={editingNews?.image_url}
              required
              className="w-full px-3 py-2 border rounded-md"
              placeholder="https://..."
            />
          </div>

          <div className="mb-4">
            <label className="flex items-center space-x-2">
              <input
                type="checkbox"
                name="is_published"
                defaultChecked={editingNews?.is_published !== false}
                className="w-4 h-4"
              />
              <span>Yayınla</span>
            </label>
          </div>

          <div className="flex space-x-3">
            <button
              type="submit"
              className="bg-[#DF6A6A] text-white px-6 py-2 rounded-md hover:bg-[#c55555]"
            >
              {editingNews ? 'Güncelle' : 'Ekle'}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                setEditingNews(null);
              }}
              className="bg-gray-300 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-400"
            >
              İptal
            </button>
          </div>
        </form>
      ) : null}

      <div className="grid gap-4">
        {news.map((item) => (
          <div key={item.id} className="bg-white p-4 rounded-lg shadow-md flex items-start space-x-4">
            <img
              src={item.image_url}
              alt={item.title_tr}
              className="w-32 h-24 object-cover rounded"
            />
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-bold text-lg">{item.title_tr}</h3>
                  <p className="text-sm text-gray-600 mt-1">{item.excerpt_tr}</p>
                  <div className="flex items-center space-x-2 mt-2">
                    {item.is_published ? (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                        <Eye className="w-3 h-3 inline mr-1" />
                        Yayında
                      </span>
                    ) : (
                      <span className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded">
                        <EyeOff className="w-3 h-3 inline mr-1" />
                        Taslak
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleEdit(item)}
                    className="text-blue-600 hover:text-blue-800"
                  >
                    <Edit2 className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(item.id)}
                    className="text-red-600 hover:text-red-800"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default NewsManagement;
